import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, ShoppingBag, Briefcase, GraduationCap } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";
import { useLang, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const StudentLoans = () => {
  const lang = useLang();
  const [loans, setLoans] = useState(null);
  const [level, setLevel] = useState("");
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/funding/loans`, { ...headers(), params: { ...(level && { level }) } }).then((r) => setLoans(r.data.loans)).catch(() => setLoans([]));
  }, [level]);

  const apply = async (loan) => {
    setApplying(loan.id);
    try {
      await axios.post(`${API}/funding/apply`, { option_id: loan.id, amount: loan.max_amount, note: level || "general" }, headers());
      toast.success(`Applied to ${loan.name} 🎓`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplying(null);
    }
  };

  if (loans === null) return null;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="student-loans">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><GraduationCap className="h-5 w-5 text-[#06B6D4]" /> Student Loan Marketplace</p>
      <p className="mt-1 text-xs text-slate-500">Compare education loans from multiple lenders.</p>
      <select data-testid="loan-level" value={level} onChange={(e) => setLevel(e.target.value)}
        className="mt-3 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
        <option value="">All levels</option>
        <option value="diploma">Diploma</option>
        <option value="undergraduate">Undergraduate</option>
        <option value="postgraduate">Postgraduate</option>
      </select>
      <div className="mt-4 space-y-2.5">
        {loans.map((l) => (
          <div key={l.id} data-testid={`loan-${l.id.slice(-6)}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">{l.name}</p>
              <p className="mt-0.5 font-jbmono text-[10px] text-slate-500">{l.provider} · {l.country} · {l.interest}</p>
            </div>
            <span className="font-jbmono text-xs font-bold text-[#22C55E]">{fmtMoney(l.max_amount, "INR", "₹")}</span>
            <button data-testid={`loan-apply-${l.id.slice(-6)}`} onClick={() => apply(l)} disabled={applying === l.id}
              className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
              {applying === l.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Apply"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export const HiringMarketplace = () => {
  const [contracts, setContracts] = useState(null);
  const [type, setType] = useState("");
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hiring`, { ...headers(), params: { ...(type && { contract_type: type }) } }).then((r) => setContracts(r.data.contracts)).catch(() => setContracts([]));
  }, [type]);

  const apply = async (c) => {
    setApplying(c.id);
    try {
      await axios.post(`${API}/hiring/${c.id}/apply`, {}, { ...headers(), params: { note: "via marketplace" } });
      toast.success(`Applied to ${c.title} ✅`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplying(null);
    }
  };

  if (contracts === null) return null;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="hiring-marketplace">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><Briefcase className="h-5 w-5 text-[#F5B300]" /> Chef Hiring Marketplace</p>
      <p className="mt-1 text-xs text-slate-500">Full-time, part-time, gig and contract roles.</p>
      <div className="mt-3 flex gap-2">
        {["", "full-time", "part-time", "gig", "contract"].map((t) => (
          <button key={t} data-testid={`hm-${t || "all"}`} onClick={() => setType(t)}
            className={`rounded-full px-3.5 py-1.5 font-jbmono text-[10px] font-bold uppercase tracking-wider ${type === t ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "border border-white/10 text-slate-400"}`}>
            {t || "All"}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-2.5">
        {contracts.map((c) => (
          <div key={c.id} data-testid={`hm-job-${c.id.slice(-6)}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">{c.title}</p>
              <p className="mt-0.5 font-jbmono text-[10px] text-slate-500">{c.company} · {c.country} · {c.contract_type} · {c.cuisine}</p>
            </div>
            <span className="font-jbmono text-xs font-bold text-[#22C55E]">{c.pay}</span>
            <button data-testid={`hm-apply-${c.id.slice(-6)}`} onClick={() => apply(c)} disabled={applying === c.id}
              className="rounded-full bg-gradient-to-r from-[#F5B300] to-[#F97316] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-[#09090B] disabled:opacity-50">
              {applying === c.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Apply"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export const GlobalMarketplace = () => {
  const lang = useLang();
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("");
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hiring/marketplace`, { ...headers(), params: { ...(category && { category }) } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [category]);

  const buy = async (item) => {
    setBuying(item.id);
    try {
      await axios.post(`${API}/hiring/marketplace/${item.id}/buy`, {}, headers());
      toast.success(`Bought ${item.title} ✅`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBuying(null);
    }
  };

  if (items === null) return null;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="global-marketplace">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><ShoppingBag className="h-5 w-5 text-[#06B6D4]" /> Global Marketplace</p>
      <p className="mt-1 text-xs text-slate-500">Buy & sell notes, recipes, equipment and services.</p>
      <select data-testid="gm-category" value={category} onChange={(e) => setCategory(e.target.value)}
        className="mt-3 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
        <option value="">All categories</option>
        <option value="equipment">Equipment</option>
        <option value="recipes">Recipes</option>
        <option value="notes">Notes</option>
        <option value="services">Services</option>
      </select>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {items.map((i) => (
          <div key={i.id} data-testid={`gm-${i.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-bold">{i.title}</p>
              <span className="font-jbmono text-xs font-bold text-[#22C55E]">{fmtMoney(i.price, "INR", "₹")}</span>
            </div>
            <p className="mt-1 font-jbmono text-[10px] text-slate-500">{i.seller_name} · {i.category} · {i.country}</p>
            <button data-testid={`gm-buy-${i.id.slice(-6)}`} onClick={() => buy(i)} disabled={buying === i.id}
              className="mt-3 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
              {buying === i.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Buy"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};
